import type { BadgeVariant } from "@dl/web-core-js/ui";
import type { CutJobDTO, CutJobStatus, ProductionDocumentKind } from "../../../api/cutJobs";

type Translate = (key: string, fallback: string, options?: Record<string, unknown>) => string;

/** Every cut job query: invalidated with ["production"] by the order pages' refresh. */
export const CUT_JOBS_KEY = ["production", "cut-job"];

export function cutJobStatusLabel(status: CutJobStatus, t: Translate): string {
  switch (status) {
    case "DRAFT":
      return t("cut-job.status.draft", "Draft");
    case "CONFIRMED":
      return t("cut-job.status.confirmed", "Confirmed");
    case "IN_PROGRESS":
      return t("cut-job.status.in-progress", "In progress");
    case "DONE":
      return t("cut-job.status.done", "Done");
    default:
      return t("cut-job.status.cancelled", "Cancelled");
  }
}

export function cutJobStatusVariant(status: CutJobStatus): BadgeVariant {
  switch (status) {
    case "DRAFT":
      return "neutral";
    case "CONFIRMED":
      return "info";
    case "IN_PROGRESS":
      return "warning";
    case "DONE":
      return "success";
    default:
      return "neutral";
  }
}

/** "Job 12 · CR-4471": the job as the floor and Cut Rite's paper name it. */
export function cutJobTitle(job: Pick<CutJobDTO, "number" | "cutrite_number">, t: Translate): string {
  const base = t("cut-job.title", "Cut job {{n}}", { n: job.number });
  return job.cutrite_number ? `${base} · ${job.cutrite_number}` : base;
}

/** The STOCK processes cut jobs are made for: the saw at Laval, the foam cutter at Terrebonne. */
export const JOB_PROCESSES = ["CUT", "FOAM"] as const;
export type JobProcess = (typeof JOB_PROCESSES)[number];

export function jobProcessLabel(code: string, t: Translate): string {
  return code === "FOAM" ? t("cut-job.process.foam", "Foam") : t("cut-job.process.cut", "Saw");
}

export function documentKindLabel(kind: ProductionDocumentKind, t: Translate): string {
  switch (kind) {
    case "CUTRITE_CUT":
      return t("document.kind.cutrite-cut", "Cut Rite: cutting plan");
    case "CUTRITE_FOAM":
      return t("document.kind.cutrite-foam", "Cut Rite: foam plan");
    default:
      return t("document.kind.other", "Other");
  }
}
